<template>
  <Adminlte>
    <div class="box box-primary center-block relatorio-estatistico">
      <div class="box-header with-border">
        <i class="fa fa-dashboard font-dark"></i>
        <h3 class="box-title">Relatório Estatístico Nacional</h3>
      </div>

      <section class="content">
        <div class="row filter-row">
          <div class="col-md-2">
            <label class="filter-label">
              Ano de referência
              <span class="filter-hint" tabindex="0">
                <i class="fa fa-question-circle"></i>
                <span class="filter-hint__bubble">{{ yearHint }}</span>
              </span>
            </label>
            <select v-model.number="year" class="form-control" :disabled="isBusy">
              <option v-for="y in availableYears" :key="y" :value="y">{{ y }}</option>
            </select>
          </div>
          <div class="col-md-2">
            <label class="filter-label">
              Ano comparativo
              <span class="filter-hint" tabindex="0">
                <i class="fa fa-question-circle"></i>
                <span class="filter-hint__bubble">{{ compareYearHint }}</span>
              </span>
            </label>
            <select v-model="compareYear" class="form-control" :disabled="isBusy">
              <option value="">Nenhum</option>
              <option v-for="y in compareYearOptions" :key="'c-' + y" :value="y">{{ y }}</option>
            </select>
          </div>
          <div class="col-md-8 filter-actions">
            <button
              type="button"
              class="btn btn-primary"
              @click="loadRelatorio"
              :disabled="isBusy"
            >
              <i class="fa fa-refresh" :class="{ 'fa-spin': loading }"></i>
              Gerar relatório
            </button>
            <button
              type="button"
              class="btn btn-success"
              @click="downloadExcel"
              :disabled="isBusy || !relatorio"
            >
              <i class="fa fa-file-excel-o" :class="{ 'fa-spin': downloading === 'excel' }"></i>
              Excel
            </button>
            <button
              type="button"
              class="btn btn-default"
              @click="downloadPdf"
              :disabled="isBusy || !relatorio"
            >
              <i class="fa fa-file-pdf-o" :class="{ 'fa-spin': downloading === 'pdf' }"></i>
              PDF
            </button>
          </div>
        </div>

        <div v-if="loading" class="alert alert-info">
          <i class="fa fa-spinner fa-spin"></i>
          Gerando relatório estatístico… isso pode levar vários minutos.
        </div>

        <div v-if="downloading" class="alert alert-info">
          <i class="fa fa-spinner fa-spin"></i>
          {{ exportStatusMessage }}
        </div>

        <div v-if="infoMessage" class="alert alert-info">{{ infoMessage }}</div>
        <div v-if="errorMessage" class="alert alert-danger">{{ errorMessage }}</div>

        <div v-if="relatorio" class="relatorio-summary">
          <div class="row">
            <div class="col-md-3">
              <div class="info-box bg-aqua">
                <span class="info-box-icon"><i class="fa fa-calendar"></i></span>
                <div class="info-box-content">
                  <span class="info-box-text">Ano</span>
                  <span class="info-box-number">{{ relatorio.year }}</span>
                </div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="info-box bg-green">
                <span class="info-box-icon"><i class="fa fa-bar-chart"></i></span>
                <div class="info-box-content">
                  <span class="info-box-text">Indicadores</span>
                  <span class="info-box-number">{{ relatorio.indicators.length }}</span>
                </div>
              </div>
            </div>
            <div class="col-md-3">
              <div class="info-box bg-yellow">
                <span class="info-box-icon"><i class="fa fa-folder-open"></i></span>
                <div class="info-box-content">
                  <span class="info-box-text">Seções</span>
                  <span class="info-box-number">{{ relatorio.sections.length }}</span>
                </div>
              </div>
            </div>
            <div class="col-md-3" v-if="summaryKpis.totalParques !== null">
              <div class="info-box bg-red">
                <span class="info-box-icon"><i class="fa fa-tree"></i></span>
                <div class="info-box-content">
                  <span class="info-box-text">Parques em operação</span>
                  <span class="info-box-number">{{ summaryKpis.totalParques }}</span>
                </div>
              </div>
            </div>
          </div>
          <p class="text-muted generated-at">
            Gerado em {{ formatGeneratedAt(relatorio.generatedAt) }}
            <span v-if="relatorio.compareYear"> · Comparativo com {{ relatorio.compareYear }}</span>
          </p>
        </div>

        <div v-if="relatorio" class="section-nav">
          <button
            v-for="section in relatorio.sections"
            :key="section.id"
            type="button"
            class="btn btn-default btn-sm section-nav-btn"
            :class="{ active: activeSectionId === section.id }"
            @click="scrollToSection(section.id)"
          >
            {{ section.label }}
          </button>
        </div>

        <div v-if="relatorio">
          <div
            v-for="section in relatorio.sections"
            :key="section.id"
            :id="'section-' + section.id"
            class="report-section panel panel-default"
          >
            <div class="panel-heading" @click="toggleSection(section.id)">
              <h3 class="panel-title">
                <i class="fa" :class="isSectionExpanded(section.id) ? 'fa-chevron-down' : 'fa-chevron-right'"></i>
                {{ section.label }}
                <span class="badge">{{ section.indicators.length }}</span>
              </h3>
            </div>
            <div v-show="isSectionExpanded(section.id)" class="panel-body">
              <IndicatorResult
                v-for="indicator in section.indicators"
                :key="indicator.id"
                :indicator="indicator"
                :chart-visible="isSectionExpanded(section.id)"
              />
            </div>
          </div>
        </div>
      </section>
    </div>
  </Adminlte>
</template>

<script>
  import Adminlte from '@/components/template/Adminlte'
  import IndicatorResult from '@/components/elements/relatorio_estatistico/IndicatorResult'
  import RelatorioEstatisticoClient from '@/services/resource_client/relatorio_estatistico'
  import { FIRST_AVAILABLE_YEAR } from '@/constants/report_period'
  import { saveAs } from 'file-saver'

  export default {
    name: 'RelatorioEstatistico',
    components: { Adminlte, IndicatorResult },
    data () {
      const currentYear = new Date().getFullYear()
      return {
        year: currentYear - 1,
        compareYear: '',
        loading: false,
        downloading: null,
        errorMessage: null,
        infoMessage: null,
        relatorio: null,
        activeSectionId: null,
        expandedSections: {},
        yearHint: 'Ano principal do relatório. Todos os indicadores, tabelas e gráficos são calculados com base neste período.',
        compareYearHint: 'Ano anterior opcional para comparação. Quando selecionado, indicadores compatíveis mostram o valor do ano de referência, do ano comparativo e a variação percentual entre eles. Escolha um ano menor que o de referência.'
      }
    },
    computed: {
      isBusy () {
        return this.loading || this.downloading !== null
      },
      exportStatusMessage () {
        if (this.downloading === 'excel') {
          return 'Gerando planilha Excel… isso pode levar vários minutos.'
        }
        if (this.downloading === 'pdf') {
          return 'Gerando PDF a partir do relatório exibido… isso pode levar alguns minutos.'
        }
        return ''
      },
      availableYears () {
        const years = []
        const end = new Date().getFullYear() + 1
        for (let y = end; y >= FIRST_AVAILABLE_YEAR; y--) {
          years.push(y)
        }
        return years
      },
      compareYearOptions () {
        return this.availableYears.filter(function (y) {
          return y < this.year
        }.bind(this))
      },
      summaryKpis () {
        if (!this.relatorio) {
          return { totalParques: null }
        }

        const evolucao = this.relatorio.indicators.find(function (item) {
          return item.id === 'evolucao_historica_parques'
        })

        if (!evolucao || !evolucao.data || !evolucao.data.snapshot) {
          return { totalParques: null }
        }

        return {
          totalParques: evolucao.data.snapshot.operacao || evolucao.data.snapshot.total || null
        }
      }
    },
    watch: {
      year (newYear) {
        if (this.compareYear && Number(this.compareYear) >= Number(newYear)) {
          this.compareYear = ''
        }
      }
    },
    methods: {
      buildParams () {
        const params = {}
        if (this.compareYear) {
          params.compareYear = this.compareYear
        }
        return params
      },
      loadRelatorio () {
        this.loading = true
        this.errorMessage = null
        this.infoMessage = null

        RelatorioEstatisticoClient.get(this.year, this.buildParams())
          .then(function (data) {
            this.relatorio = data
            this.expandedSections = {}
            if (data.sections.length > 0) {
              this.expandedSections[data.sections[0].id] = true
              this.activeSectionId = data.sections[0].id
            }
          }.bind(this))
          .catch(function (error) {
            if (error.response && error.response.data && error.response.data.errors) {
              this.errorMessage = error.response.data.errors.join(', ')
              return
            }
            if (error.response && error.response.status === 401) {
              this.errorMessage = 'Sessão expirada. Faça login novamente.'
              return
            }
            if (error.code === 'ECONNABORTED' || !error.response) {
              this.errorMessage = 'A requisição demorou demais ou foi interrompida. Tente novamente ou escolha um ano anterior.'
              return
            }
            this.errorMessage = 'Não foi possível carregar o relatório estatístico.'
          }.bind(this))
          .finally(function () {
            this.loading = false
          }.bind(this))
      },
      downloadExcel () {
        this.downloadFile('excel', 'relatorio-estatistico-' + this.year + '.xlsx')
      },
      downloadPdf () {
        this.downloadFile('pdf', 'relatorio-estatistico-' + this.year + '.pdf')
      },
      downloadFile (type, filename) {
        const self = this
        this.downloading = type
        this.errorMessage = null
        this.infoMessage = null

        const download = type === 'excel'
          ? RelatorioEstatisticoClient.downloadExcelFromPayload(this.year, this.relatorio)
          : RelatorioEstatisticoClient.downloadPdfFromPayload(this.year, this.relatorio)

        download
          .then(function (response) {
            const contentType = (response.headers['content-type'] || '').toLowerCase()

            if (contentType.indexOf('json') !== -1) {
              return response.data.text().then(function (text) {
                let message = 'Não foi possível baixar o arquivo.'
                try {
                  const payload = JSON.parse(text)
                  if (payload.errors && payload.errors.length > 0) {
                    message = payload.errors.join(', ')
                  }
                } catch (error) {
                  // keep default message
                }
                throw new Error(message)
              })
            }

            if (type === 'pdf') {
              if (contentType.indexOf('html') !== -1 || response.preparedFallback === 'html') {
                const htmlFilename = response.preparedFilename || filename.replace(/\.pdf$/i, '.html')
                if (self.openHtmlExportInNewTab(response.data)) {
                  self.infoMessage = 'Relatório aberto em nova aba. Use Imprimir > Salvar como PDF, se desejar.'
                } else {
                  saveAs(response.data, htmlFilename)
                  self.errorMessage = 'Não foi possível abrir nova aba. O arquivo HTML foi baixado — abra-o e use Imprimir > Salvar como PDF.'
                }
                return
              }

              if (contentType.indexOf('pdf') === -1) {
                throw new Error('O servidor não retornou um arquivo PDF válido.')
              }

              saveAs(response.data, response.preparedFilename || filename)
              return
            }

            if (type === 'excel' && contentType.indexOf('html') !== -1) {
              throw new Error('O servidor retornou HTML em vez de Excel. Tente gerar o relatório novamente.')
            }

            if (contentType.indexOf('html') !== -1) {
              throw new Error(
                'O servidor retornou uma página de erro em vez do arquivo. ' +
                'Tente novamente em alguns instantes.'
              )
            }

            saveAs(response.data, response.preparedFilename || filename)
          })
          .catch(function (error) {
            const data = error && error.response && error.response.data
            if (data && typeof data.text === 'function') {
              data.text().then(function (text) {
                let message = null
                try {
                  const payload = JSON.parse(text)
                  if (payload.errors && payload.errors.length > 0) {
                    message = payload.errors.join(', ')
                  }
                } catch (parseError) {
                  // ignore
                }
                self.errorMessage = message || self.resolveDownloadError(error, type)
              }).catch(function () {
                self.errorMessage = self.resolveDownloadError(error, type)
              })
              return
            }

            self.errorMessage = self.resolveDownloadError(error, type)
          })
          .finally(function () {
            self.downloading = null
          })
      },
      toggleSection (sectionId) {
        const willExpand = !this.expandedSections[sectionId]
        this.$set(this.expandedSections, sectionId, willExpand)
        this.activeSectionId = sectionId
        if (willExpand) {
          this.$nextTick(function () {
            window.dispatchEvent(new Event('resize'))
          })
        }
      },
      isSectionExpanded (sectionId) {
        return !!this.expandedSections[sectionId]
      },
      scrollToSection (sectionId) {
        this.$set(this.expandedSections, sectionId, true)
        this.activeSectionId = sectionId
        const element = document.getElementById('section-' + sectionId)
        if (element) {
          element.scrollIntoView({ behavior: 'smooth', block: 'start' })
        }
        this.$nextTick(function () {
          window.dispatchEvent(new Event('resize'))
        })
      },
      formatGeneratedAt (isoDate) {
        if (!isoDate) {
          return ''
        }
        return new Date(isoDate).toLocaleString('pt-BR')
      },
      openHtmlExportInNewTab (blob) {
        const htmlBlob = blob instanceof Blob
          ? (blob.type ? blob : new Blob([blob], { type: 'text/html;charset=utf-8' }))
          : new Blob([blob], { type: 'text/html;charset=utf-8' })
        const url = URL.createObjectURL(htmlBlob)
        const newWindow = window.open(url, '_blank')

        if (!newWindow) {
          URL.revokeObjectURL(url)
          return false
        }

        window.setTimeout(function () {
          URL.revokeObjectURL(url)
        }, 60000)

        return true
      },
      resolveDownloadError (error, type) {
        const label = type === 'pdf' ? 'PDF' : 'Excel'

        if (!error) {
          return 'Não foi possível baixar o ' + label + '.'
        }

        if (error.code === 'ECONNABORTED' || !error.response) {
          if (error.message && error.message !== 'Network Error') {
            return error.message
          }

          return 'A geração do ' + label +
            ' falhou na conexão com o servidor. Gere o relatório novamente e tente outra vez.'
        }

        if (error.response.status === 401) {
          return 'Sessão expirada. Faça login novamente.'
        }

        if (error.response.status === 403) {
          return 'O servidor bloqueou o download do ' + label +
            ' (limite do hosting). Tente novamente em alguns instantes.'
        }

        if (error.response.status === 404) {
          return 'Arquivo de exportação expirado. Gere o relatório novamente e tente outra vez.'
        }

        const data = error.response.data
        if (data && data.errors && data.errors.length > 0) {
          return data.errors.join(', ')
        }

        if (error.message && error.message !== 'Network Error') {
          return error.message
        }

        return 'Não foi possível baixar o ' + label + '.'
      }
    }
  }
</script>

<style scoped>
  .filter-row {
    margin-bottom: 20px;
  }
  .filter-label {
    display: inline-flex;
    align-items: center;
    gap: 6px;
  }
  .filter-hint {
    position: relative;
    display: inline-flex;
    color: #999;
    cursor: help;
    outline: none;
  }
  .filter-hint:hover,
  .filter-hint:focus {
    color: #3c8dbc;
  }
  .filter-hint__bubble {
    display: none;
    position: absolute;
    left: 0;
    top: calc(100% + 8px);
    z-index: 1080;
    width: 280px;
    padding: 10px 12px;
    border-radius: 4px;
    background: #2f3e4d;
    color: #fff;
    font-size: 12px;
    font-weight: normal;
    line-height: 1.45;
    text-align: left;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }
  .filter-hint__bubble::before {
    content: '';
    position: absolute;
    top: -6px;
    left: 10px;
    border-width: 0 6px 6px 6px;
    border-style: solid;
    border-color: transparent transparent #2f3e4d transparent;
  }
  .filter-hint:hover .filter-hint__bubble,
  .filter-hint:focus .filter-hint__bubble {
    display: block;
  }
  .filter-actions {
    padding-top: 24px;
  }
  .filter-actions .btn {
    margin-right: 8px;
  }
  .section-nav {
    position: sticky;
    top: 50px;
    z-index: 1020;
    background: #fff;
    padding: 10px 0 12px;
    margin-bottom: 20px;
    border-bottom: 1px solid #e3e3e3;
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.06);
  }
  .section-nav-btn {
    margin: 0 6px 6px 0;
  }
  .section-nav-btn.active {
    background-color: #3c8dbc;
    color: #fff;
    border-color: #367fa9;
  }
  .report-section .panel-heading {
    cursor: pointer;
  }
  .report-section .panel-title .badge {
    margin-left: 8px;
  }
  .generated-at {
    margin-bottom: 20px;
  }
</style>



// WEBPACK FOOTER //
// RelatorioEstatistico.vue?b13ac1da